import { isNotNullish } from 'effect/Predicate'

import type { PolicyAction } from './action.ts'
import { defaultPoliciesMapper } from './defaultPolicies.ts'
import type { Policy, PolicySubject, PolicyUserRole, ViewPermissionUser, ViewPermissionUserGroup } from './schema.ts'

// eslint-disable-next-line sonarjs/todo-tag
// TODO: ここでエラー定義してしまって良いだろうか？
export class ForbiddenError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'ForbiddenError'
  }
}

// ユーザー以外のabilityも今後実装する可能性があるので、抽象化しておく
abstract class Ability {
  public abstract can(action: PolicyAction, subject?: PolicySubject): boolean | Promise<boolean>

  public async cannot(action: PolicyAction, subject?: PolicySubject) {
    return !(await this.can(action, subject))
  }

  public abstract throwIfCannot(action: PolicyAction, subject?: PolicySubject): Promise<void>
}

interface AbilityUser {
  id: string
  role: PolicyUserRole
  organizationId: string // 不要かも
  userGroupIds?: string[] | null
}

interface PolicyResolverArgument {
  action: PolicyAction
  subject?: PolicySubject
}

interface PolicyResolver {
  resolve: (x: PolicyResolverArgument) => Promise<PoliciesWithViewPermission>
}

interface PolicyResolverSync {
  resolve: (x: PolicyResolverArgument) => PoliciesWithViewPermission
}

interface PoliciesWithViewPermission {
  policies: Policy[]
  viewPermissionUsers: ViewPermissionUser[]
  viewPermissionUserGroups: ViewPermissionUserGroup[]
}

abstract class UserAbilityBase extends Ability {
  protected readonly user: AbilityUser

  public constructor(user: AbilityUser) {
    super()
    this.user = user
  }

  public async throwIfCannot(action: PolicyAction, subject?: PolicySubject): Promise<void> {
    if (await this.cannot(action, subject)) {
      throw new ForbiddenError(
        `この操作を行う権限がありません: ${JSON.stringify({
          user: this.user,
          action,
          'subject.id': subject?.id,
          'subject.entityType': subject?.entityType,
        })}`,
      )
    }
  }

  static addDefaultPolicies({ action, subject }: PolicyResolverArgument, policies: Policy[]): Policy[] {
    if (subject !== undefined) {
      // この辺のロジックがやや煩雑だろうか？
      return policies
    }
    const defaultPolicies: Policy[] = defaultPoliciesMapper[action] ?? []
    // subjectがないとき、policiesにはカスタム権限や組織に紐づかないデフォルト権限が含まれる
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: カスタム権限機能の安定稼働確認まで保険としてハードコードされたデフォルトポリシー設定も含める。
    //       - 問題発生時などの対応を最小限にとどめるため
    //       - リリース直後、デフォルト権限の参照先が変わる際にユーザー影響を発生させないため
    return [...policies, ...defaultPolicies].filter(isNotNullish)
  }

  // eslint-disable-next-line @typescript-eslint/max-params
  protected decideCan(
    subject: PolicySubject | undefined,
    policiesWithSubject: Policy[],
    policiesWithoutSubject: Policy[],
    viewPermissionUsers: ViewPermissionUser[],
    viewPermissionUserGroups: ViewPermissionUserGroup[],
  ): boolean {
    // adminは常になんでもできる
    if (this.user.role === `admin`) {
      return true
    }
    // minimalは常に何もできない
    if (this.user.role === `minimal`) {
      return false
    }

    // canの対象があるとき
    if (subject !== undefined) {
      // viewに対して個別に権限設定されている場合はその設定を優先し、それ以外は評価対象としない
      // e.g. member権限でKPIの編集権限がなかったとしても、個別に指定されている場合は編集可能とする
      const shouldUseViewPermission = policiesWithSubject.some(
        (policy) => policy.principal.type === 'user' && policy.permission && policy.subject?.id === subject.id,
      )
      if (shouldUseViewPermission) {
        return this.canActionUserOrUserGroup(subject, viewPermissionUsers, viewPermissionUserGroups)
      }

      // 複数ポリシーが設定されているケースは想定しない
      const rolePolicy = policiesWithSubject.find(
        (policy) => policy.principal.type === 'userRole' && policy.principal.role === this.user.role,
      )
      const createdByPolicy = policiesWithSubject.find(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (policy) => policy.principal.type === 'userAttribute' && policy.principal.attributeType === 'createdBy',
      ) // createdBy以外のケースがでたとき、優先順位をどうするかは未検討
      const everyonePolicy = policiesWithSubject.find((policy) => policy.principal.type === 'everyone')

      // 非常にややこしいが、「作成者」の判定では、ポリシー側ではsubjectはなしで、canの対象にはsubjectありを想定するようなポリシーがありうる
      // 「XXXというレコードの作成者に対するポリシー」と「作成者に対するポリシー」の2パターンあり、これは後者
      const createdByPolicyWithoutPolicySubject = policiesWithoutSubject.find(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (policy) => policy.principal.type === 'userAttribute' && policy.principal.attributeType === 'createdBy',
      )

      // ユーザー、作成者、ロール、デフォルト、作成者(ポリシー側のsubjectなし)の順に評価
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: 厳密には、作成者(ポリシー側のsubjectなし)は「ユーザー（ポリシー側のsubjectなし）=L114以降）」で評価されるべきだが、現状userごとの権限がないので一旦無視
      const matchedPolicy = [
        subject.createdById === this.user.id ? createdByPolicy : undefined,
        rolePolicy,
        everyonePolicy,
        subject.createdById === this.user.id ? createdByPolicyWithoutPolicySubject : undefined,
      ].find(isNotNullish)

      const policy = matchedPolicy
      if (policy !== undefined) {
        return policy.permission
      }
    }

    const rolePolicy = policiesWithoutSubject.find(
      (policy) => policy.principal.type === 'userRole' && policy.principal.role === this.user.role,
    )
    const everyonePolicy = policiesWithoutSubject.find((policy) => policy.principal.type === 'everyone')
    // ロール、デフォルトの順に評価（ほぼロールで決まるはず）
    const policy = [rolePolicy, everyonePolicy].find(isNotNullish)
    if (policy !== undefined) {
      return policy.permission
    }
    return false
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public canActionUserOrUserGroup(
    subject: PolicySubject,
    viewPermissionUsers: ViewPermissionUser[],
    viewPermissionUserGroups: ViewPermissionUserGroup[],
  ) {
    // 個別にユーザー/グループが設定されている場合において、managrは常に操作可能
    if (this.user.role === `manager`) {
      return true
    }
    if (viewPermissionUsers.some((x) => x.userId === this.user.id && x.viewId === subject.id)) {
      return true
    }
    if (isNotNullish(this.user.userGroupIds)) {
      for (const userGroupId of this.user.userGroupIds) {
        if (viewPermissionUserGroups.some((x) => x.userGroupId === userGroupId && x.viewId === subject.id)) {
          return true
        }
      }
      if (this.user.userGroupIds.length === 0) {
        for (const viewPermissionuserGroup of viewPermissionUserGroups) {
          if (viewPermissionuserGroup.userGroupId === `no-team` && viewPermissionuserGroup.viewId === subject.id) {
            return true
          }
        }
      }
    }
    return false
  }
}

export class UserAbilityAsync extends UserAbilityBase {
  private readonly resolver: PolicyResolver

  constructor(user: AbilityUser, resolver: PolicyResolver) {
    super(user)
    this.resolver = resolver
  }

  private async resolve(policyResolverArgument: PolicyResolverArgument): Promise<PoliciesWithViewPermission> {
    const policiesWithViewPermission = await this.resolver.resolve(policyResolverArgument)
    return {
      policies: UserAbilityBase.addDefaultPolicies(policyResolverArgument, policiesWithViewPermission.policies),
      viewPermissionUsers: policiesWithViewPermission.viewPermissionUsers,
      viewPermissionUserGroups: policiesWithViewPermission.viewPermissionUserGroups,
    }
  }

  public async can(action: PolicyAction, subject?: PolicySubject): Promise<boolean> {
    const policiesWithSubject: PoliciesWithViewPermission = await this.resolve({
      action,
      subject,
    })
    const policiesWithoutSubject: PoliciesWithViewPermission = await this.resolve({
      action,
    })
    return this.decideCan(
      subject,
      policiesWithSubject.policies,
      policiesWithoutSubject.policies,
      policiesWithSubject.viewPermissionUsers,
      policiesWithSubject.viewPermissionUserGroups,
    )
  }
}

export class UserAbility extends UserAbilityBase {
  private readonly resolver: PolicyResolverSync

  constructor(user: AbilityUser, resolver: PolicyResolverSync) {
    super(user)
    this.resolver = resolver
  }

  private resolve(policyResolverArgument: PolicyResolverArgument): PoliciesWithViewPermission {
    const policiesWithViewPermission = this.resolver.resolve(policyResolverArgument)
    return {
      policies: UserAbilityBase.addDefaultPolicies(policyResolverArgument, policiesWithViewPermission.policies),
      viewPermissionUsers: policiesWithViewPermission.viewPermissionUsers,
      viewPermissionUserGroups: policiesWithViewPermission.viewPermissionUserGroups,
    }
  }

  public can(action: PolicyAction, subject?: PolicySubject): boolean {
    const policiesWithSubject: PoliciesWithViewPermission = this.resolve({
      action,
      subject,
    })
    const policiesWithoutSubject: PoliciesWithViewPermission = this.resolve({
      action,
    })
    return this.decideCan(
      subject,
      policiesWithSubject.policies,
      policiesWithoutSubject.policies,
      policiesWithSubject.viewPermissionUsers,
      policiesWithSubject.viewPermissionUserGroups,
    )
  }
}
